import React, { useState, useEffect, useMemo, useCallback } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { Typography, Box, Paper, Stack, IconButton } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { ArrowBackIos, UnfoldMore, UnfoldLess } from "@mui/icons-material";
import moment from "moment";
import { theme, BoxStyle, BackArrowStyle } from "services/constants";
import { customGetAPI } from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import { FilterProvider } from "pages/FilterContext";
import { useSaveView } from "components/CustomComponents/useSaveView";
import { checkViewerPermission } from "services/utils/HelperFunction";
import BSDateRangePicker from "pages/PayRoll/PayInputs/ShiftEarnings/DateRangePicker";
import {
  OTReportV2Table,
  TOTAL_BAND_LABEL,
  formatOTBandLabel,
} from "./OTReportV2Table";

// final_approved_ot arrives from the API as an "HH:MM" string (e.g. "00:22").
// Convert it to total minutes so durations can be summed per day/band, then
// formatted back to "HH:MM" for display.
const hhmmToMinutes = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const stringValue = value.toString().trim();
  if (stringValue.includes(":")) {
    const [hoursPart, minutesPart] = stringValue.split(":");
    const hours = Number(hoursPart) || 0;
    const minutes = Number(minutesPart) || 0;
    return hours * 60 + minutes;
  }
  const parsedValue = Number(stringValue);
  // A bare number is treated as decimal hours (fallback only).
  return Number.isFinite(parsedValue) ? Math.round(parsedValue * 60) : 0;
};

// Render total minutes back as a zero-padded "HH:MM" string ("00:22").
const minutesToHHMM = (totalMinutes) => {
  const safeMinutes = Math.max(0, Math.round(totalMinutes || 0));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  const pad = (part) => part.toString().padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}`;
};

const toNumericAmount = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

// Pick the OT quantity ("HH:MM") to roll up for a record based on its band:
//   - Default Band (band_name null/empty or the literal "Default Band"):
//     use final_approved_ot (the approved OT for the default band).
//   - Any other band: use the band's credited_quantity, falling back to
//     final_approved_ot when the record has none (e.g. normalized shift
//     Weekoff/Holiday records, which only carry a quantity).
const resolveOTQuantity = (record) => {
  const bandName = record?.band_name;
  const isDefaultBand =
    bandName === null ||
    bandName === undefined ||
    bandName === "" ||
    bandName === "Default Band";
  if (isDefaultBand) {
    return record?.final_approved_ot;
  }
  return record?.credited_quantity ?? record?.final_approved_ot;
};

// Shift earning records that count as OT for this report: worked week-offs and
// holidays. Everything else in the shift earning API is a normal shift and is
// ignored here.
const OT_SHIFT_EARNING_TYPES = new Set([
  "Weekoff Attendance",
  "Holiday Attendance",
]);

// The shift earning API uses different field names than the OT API. Reshape the
// relevant Weekoff/Holiday records into the same record shape the OT rollup
// consumes so both sources flow through the exact same pivot:
//   quantity -> final_approved_ot (per-day HH:MM), net_pay -> earning_amount
//   (money), band_name/employee/date stay as-is. Per employee+date the data
//   lives in only one of the two APIs, so the pivot can safely concatenate
//   both sets.
const normalizeShiftRecords = (shiftRecords) =>
  (Array.isArray(shiftRecords) ? shiftRecords : [])
    .filter((record) => OT_SHIFT_EARNING_TYPES.has(record?.earning_type))
    .map((record) => ({
      employee_code: record?.employee_code ?? "",
      employee_name: record?.employee_name ?? "",
      date: record?.date,
      band_name: record?.band_name,
      final_approved_ot: record?.quantity,
      earning_amount: record?.net_pay,
    }));

// API serializes the OT date as "DD-MM-YYYY". Normalise it to "YYYY-MM-DD" so it
// can be matched against the generated date columns.
const normalizeRecordDate = (value) => {
  if (!value) {
    return "";
  }
  const parsed = moment(value, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

export const OTReportV2 = ({ setShowReport, reportName }) => {
  const {
    domain,
    topbarCompanyId,
    topbarCompanyName,
    userDetails,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const { downloadGroupedExcel, downloadGroupedCsv, downloadGroupedPdf } =
    useExcelDownload();

  const reportsModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );
  const subModuleId = reportsModule?.submodules?.find(
    (sub) => sub.sub_module_name === "OT Report",
  )?.sub_module_id;
  // Read-only report -- filter/sort/download/save-view are view-level actions,
  // not edit actions. Gate on viewer permission (see EPFReport/index.js for
  // the fuller reasoning); editor is always false for Reports submodules in
  // the live payload and was blocking these actions for every user.
  const check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "OT Report",
  );

  const [overtimeData, setOvertimeData] = useState([]);
  const [otReportData, setOtReportData] = useState([]);
  // Default the report to the current month, since OT is reviewed over a range.
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("month").format("YYYY-MM-DD"),
  );
  const [loader, setLoader] = useState(false);
  // Which employee rollup rows are expanded to show their per-band breakdown.
  // Owned here (not in the table) so the toolbar "expand all" button can drive
  // the same state as the per-row chevrons.
  const [expandedEmps, setExpandedEmps] = useState(() => new Set());

  const callUpdatedApi = useCallback(
    async (start_date, end_date) => {
      if (!topbarCompanyId || !start_date || !end_date) {
        return;
      }
      setLoader(true);
      // Two sources feed this report: the OT earning API and the shift earning
      // API (Weekoff/Holiday worked days). Fetch both for the same range in
      // parallel and merge them into one flat record set for the pivot.
      const [otRes, shiftRes] = await Promise.all([
        customGetAPI(
          `${domain}payroll/earning/overtime/earning_details/?company_id=${topbarCompanyId}&start_date=${start_date}&end_date=${end_date}`,
        ),
        customGetAPI(
          `${domain}payroll/earning/shift/earning_details/?company_id=${topbarCompanyId}&start_date=${start_date}&end_date=${end_date}`,
        ),
      ]);
      const otRecords =
        otRes?.ok && Array.isArray(otRes?.responseDetails)
          ? otRes.responseDetails
          : [];
      const shiftRecords = normalizeShiftRecords(
        shiftRes?.ok ? shiftRes?.responseDetails : [],
      );
      setOvertimeData([...otRecords, ...shiftRecords]);
      setLoader(false);
    },
    [domain, topbarCompanyId],
  );

  // BSDateRangePicker fires handleStartDate on mount with its default range, so
  // the initial API call is triggered from here (no separate effect needed).
  const handleDateRange = useCallback(
    (startDateValue, endDateValue) => {
      const start_date = moment(startDateValue, "DD/MM/YYYY").format(
        "YYYY-MM-DD",
      );
      const end_date = moment(endDateValue, "DD/MM/YYYY").format("YYYY-MM-DD");
      setStartDate(start_date);
      setEndDate(end_date);
      callUpdatedApi(start_date, end_date);
    },
    [callUpdatedApi],
  );

  useEffect(() => {
    if (topbarCompanyId && startDate && endDate) {
      callUpdatedApi(startDate, endDate);
    }
    // Re-fetch when the selected company changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topbarCompanyId]);

  // Build a column for every day within the selected date range.
  const dateColumns = useMemo(() => {
    if (!startDate || !endDate) {
      return [];
    }
    const start = moment(startDate, "YYYY-MM-DD");
    const end = moment(endDate, "YYYY-MM-DD");
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
      return [];
    }
    const columns = [];
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, "day")) {
      columns.push({
        key: cursor.format("YYYY-MM-DD"),
        label: cursor.format("D"), // day number only: 1, 2, 3 ...
      });
      cursor.add(1, "day");
    }
    return columns;
  }, [startDate, endDate]);

  // Human readable "month + selected range" shown next to the report title.
  const rangeLabel = useMemo(() => {
    if (!startDate || !endDate) {
      return "";
    }
    const start = moment(startDate, "YYYY-MM-DD");
    const end = moment(endDate, "YYYY-MM-DD");
    if (!start.isValid() || !end.isValid()) {
      return "";
    }
    const monthLabel = start.isSame(end, "month")
      ? start.format("MMMM YYYY")
      : `${start.format("MMM YYYY")} - ${end.format("MMM YYYY")}`;
    return `${monthLabel}  (${start.format("DD/MM/YYYY")} - ${end.format(
      "DD/MM/YYYY",
    )})`;
  }, [startDate, endDate]);

  // Pivot the flat OT records into a per-employee rollup:
  //   - one "Total" row  = daily OT summed across every band, the employee's
  //     TotalHrs, TotalCount (sum of band counts) and Amount (sum of band
  //     amounts).
  //   - one row per OT band (OT / 1.5 / 2 ...) holding that band's daily OT,
  //     hours, count and amount. Band counts and amounts sum up to the Total
  //     row by construction.
  // Rows are flattened (total row first, then its band rows) so the shared
  // CustomFilter toolbar / export can consume them, while the table regroups
  // them per employee.
  const reportRows = useMemo(() => {
    const employeesByCode = new Map();
    const employeeOrder = [];

    (overtimeData || []).forEach((record) => {
      const bandName = record?.band_name || "Default Band";
      const empId = record?.employee_code ?? "";
      const empName = record?.employee_name ?? "";
      const dateKey = normalizeRecordDate(record?.date);
      const minutes = hhmmToMinutes(resolveOTQuantity(record));
      const earningAmount = toNumericAmount(record?.earning_amount);

      if (!employeesByCode.has(empId)) {
        employeesByCode.set(empId, {
          empId,
          empName,
          bands: new Map(),
          bandOrder: [],
        });
        employeeOrder.push(empId);
      }
      const employee = employeesByCode.get(empId);
      if (!employee.empName && empName) {
        employee.empName = empName;
      }

      if (!employee.bands.has(bandName)) {
        employee.bands.set(bandName, {
          bandName,
          dateMinutes: {},
          totalMinutes: 0,
          count: 0,
          earning_amount: 0,
        });
        employee.bandOrder.push(bandName);
      }
      const band = employee.bands.get(bandName);
      if (dateKey) {
        band.dateMinutes[dateKey] = (band.dateMinutes[dateKey] || 0) + minutes;
      }
      band.totalMinutes += minutes;
      // A "count" is one OT instance/record; employee TotalCount is the sum of
      // its band counts so the breakdown always reconciles with the total.
      band.count += 1;
      band.earning_amount += earningAmount;
    });

    const rows = [];
    employeeOrder.forEach((empId) => {
      const employee = employeesByCode.get(empId);
      const bands = employee.bandOrder
        .map((name) => employee.bands.get(name))
        .sort((first, second) =>
          (first.bandName || "").localeCompare(second.bandName || ""),
        );

      // Employee-level daily sums + grand totals across bands.
      const totalDateMinutes = {};
      let totalMinutes = 0;
      let totalCount = 0;
      let totalEarningAmount = 0;
      bands.forEach((band) => {
        Object.entries(band.dateMinutes).forEach(([key, mins]) => {
          totalDateMinutes[key] = (totalDateMinutes[key] || 0) + mins;
        });
        totalMinutes += band.totalMinutes;
        totalCount += band.count;
        totalEarningAmount += band.earning_amount;
      });

      const toDateValues = (dateMinutes) => {
        const dateValues = {};
        Object.entries(dateMinutes).forEach(([key, mins]) => {
          if (mins > 0) {
            dateValues[key] = minutesToHHMM(mins);
          }
        });
        return dateValues;
      };

      // Total (rollup) row for the employee.
      rows.push({
        rowId: `${empId}||__total__`,
        empId: employee.empId,
        empName: employee.empName,
        band: TOTAL_BAND_LABEL,
        isTotal: true,
        ...toDateValues(totalDateMinutes),
        totalHrs: minutesToHHMM(totalMinutes),
        count: totalCount,
        earning_amount: totalEarningAmount,
      });

      // One breakdown row per band.
      bands.forEach((band) => {
        rows.push({
          rowId: `${empId}||${band.bandName}`,
          empId: employee.empId,
          empName: employee.empName,
          band: band.bandName,
          isTotal: false,
          ...toDateValues(band.dateMinutes),
          totalHrs: minutesToHHMM(band.totalMinutes),
          count: band.count,
          earning_amount: band.earning_amount,
        });
      });
    });

    return rows;
  }, [overtimeData]);

  // Header definitions consumed by the CustomFilter toolbar (search / filter /
  // sort / column selector) and by the Excel/CSV/PDF export.
  const filterHeaders = useMemo(
    () => [
      { name: "Emp Id", key: "empId", type: "string" },
      { name: "Name", key: "empName", type: "string" },
      { name: "Band", key: "band", type: "string" },
      ...dateColumns.map((column) => ({
        name: column.label,
        key: column.key,
        type: "string",
      })),
      { name: "TotalHrs", key: "totalHrs", type: "string" },
      { name: "TotalCount", key: "count", type: "number" },
      { name: "Amount", key: "earning_amount", type: "number" },
    ],
    [dateColumns],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || userDetails?.company_name || "N/A",
      "Report Name": reportName || "Overtime (OT)",
      "Date Range": rangeLabel || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [rangeLabel, reportName, topbarCompanyName, userDetails?.company_name],
  );

  // Saved header views (create / edit / rename / reorder / delete) — persisted
  // per user + submodule. A distinct storage key keeps this report's views
  // separate from the legacy OT report.
  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "ot_report_v2_saved_views",
    fullHeaders: filterHeaders,
    screenAccess: check_access,
  });

  // The rendered table reflects whatever the toolbar produces — CustomFilter
  // applies the search text, column filters and sort rules, then hands back the
  // resulting rows here (it also fires once on mount with the unfiltered set).
  const handleFilteredDataChange = useCallback((filteredData) => {
    setOtReportData(filteredData);
  }, []);

  const dateColumnKeys = useMemo(
    () => new Set(dateColumns.map((column) => column.key)),
    [dateColumns],
  );

  const buildExportHeader = useCallback(
    (header) => ({
      ...header,
      customFormatter: (row) => {
        if (header.key === "band") {
          return formatOTBandLabel(row?.band);
        }
        if (header.key === "empId") {
          return row?.empId || "-";
        }
        if (header.key === "empName") {
          return row?.empName || "-";
        }
        if (header.key === "totalHrs") {
          return row?.totalHrs || "";
        }
        if (header.key === "count") {
          if (row?.count || row?.count === 0) {
            return row.count;
          }
          return "";
        }
        if (header.key === "earning_amount") {
          const earningAmount = Number(row?.earning_amount);
          return Number.isFinite(earningAmount) ? earningAmount : "";
        }
        if (dateColumnKeys.has(header.key)) {
          return row?.[header.key] || "";
        }
        return row?.[header.key] ?? "";
      },
    }),
    [dateColumnKeys],
  );

  const getGroupedExportHeaders = useCallback(() => {
    const summaryHeaders = visibleHeaders
      .filter((header) => header.key !== "band")
      .map(buildExportHeader);

    const bandHeader = filterHeaders.find((header) => header.key === "band");
    const detailHeaders = [
      bandHeader,
      ...visibleHeaders.filter(
        (header) =>
          header.key !== "band" &&
          header.key !== "empId" &&
          header.key !== "empName",
      ),
    ]
      .filter(Boolean)
      .filter(
        (header, index, headers) =>
          headers.findIndex((item) => item.key === header.key) === index,
      )
      .map(buildExportHeader);

    return { summaryHeaders, detailHeaders };
  }, [buildExportHeader, filterHeaders, visibleHeaders]);

  const handleOTReportDownload = useCallback(
    async (rows, onProgress, downloadFormat = "excel") => {
      const visibleRows = Array.isArray(rows) ? rows : [];
      const totalsByEmployee = new Map(
        reportRows
          .filter((row) => row?.isTotal)
          .map((row) => [`${row.empId}`, row]),
      );
      const groupsByEmployee = new Map();
      const employeeOrder = [];

      visibleRows.forEach((row) => {
        const employeeKey = `${row?.empId || ""}`;
        if (!employeeKey) {
          return;
        }
        if (!groupsByEmployee.has(employeeKey)) {
          groupsByEmployee.set(employeeKey, {
            summaryRow: null,
            detailRows: [],
          });
          employeeOrder.push(employeeKey);
        }
        const group = groupsByEmployee.get(employeeKey);
        if (row?.isTotal) {
          group.summaryRow = row;
        } else {
          group.detailRows.push(row);
        }
      });

      const groups = employeeOrder
        .map((employeeKey) => {
          const group = groupsByEmployee.get(employeeKey);
          return {
            summaryRow:
              group.summaryRow ||
              totalsByEmployee.get(employeeKey) ||
              group.detailRows[0],
            detailRows: group.detailRows,
          };
        })
        .filter((group) => group.summaryRow);

      const { summaryHeaders, detailHeaders } = getGroupedExportHeaders();
      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadGroupedPdf
          : downloadFormat === "csv"
            ? downloadGroupedCsv
            : downloadGroupedExcel;

      await exportToFormat(
        groups,
        summaryHeaders,
        detailHeaders,
        reportName || "Overtime (OT)",
        reportMetadata,
        onProgress,
      );
    },
    [
      downloadGroupedCsv,
      downloadGroupedExcel,
      downloadGroupedPdf,
      getGroupedExportHeaders,
      reportMetadata,
      reportName,
      reportRows,
    ],
  );

  // Employee groups rebuilt from the currently displayed rows (in their current
  // order) so filtering/search/sort never leaves an empty employee block behind
  // and keeps each employee's total + band rows together.
  const visibleEmployeeIds = useMemo(() => {
    const seen = [];
    otReportData.forEach((row) => {
      if (!seen.includes(row.empId)) {
        seen.push(row.empId);
      }
    });
    return seen;
  }, [otReportData]);

  // Toggle a single employee's breakdown (driven by the row chevron).
  const toggleEmp = useCallback((empId) => {
    setExpandedEmps((prev) => {
      const next = new Set(prev);
      if (next.has(empId)) {
        next.delete(empId);
      } else {
        next.add(empId);
      }
      return next;
    });
  }, []);

  // "Expand all" is active only when every currently visible employee is open;
  // the toolbar button then flips to "collapse all".
  const allExpanded =
    visibleEmployeeIds.length > 0 &&
    visibleEmployeeIds.every((empId) => expandedEmps.has(empId));

  const toggleExpandAll = useCallback(() => {
    setExpandedEmps(allExpanded ? new Set() : new Set(visibleEmployeeIds));
  }, [allExpanded, visibleEmployeeIds]);

  // Rendered inside the CustomFilter toolbar (this report only) alongside the
  // search / download / sort / filter / columns icons.
  const expandAllAction = (
    <Mui.Tooltip
      title={allExpanded ? "Collapse all" : "Expand all"}
      arrow
      placement="top"
    >
      <span>
        <IconButton
          onClick={toggleExpandAll}
          disabled={visibleEmployeeIds.length === 0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            color: "text.secondary",
          }}
        >
          {allExpanded ? <UnfoldLess /> : <UnfoldMore />}
        </IconButton>
      </span>
    </Mui.Tooltip>
  );

  return (
    <ThemeProvider theme={theme}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box
          sx={{
            ...BoxStyle,
            height: "calc(100vh - 72px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton
                onClick={() => setShowReport(false)}
                sx={BackArrowStyle}
              >
                <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
              </IconButton>
              <Box>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ flexWrap: "wrap", mb: 0.5 }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                  >
                    {reportName || "Overtime (OT)"}
                  </Typography>
                  {rangeLabel && (
                    <Mui.Chip
                      label={rangeLabel}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 600, borderRadius: "6px" }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Overtime rollup per employee: a total row of daily OT hours,
                  total count and amount, broken down into one row per OT band.
                </Typography>
              </Box>
            </Stack>
            <Box>
              <BSDateRangePicker
                handleStartDate={handleDateRange}
                showToday={true}
                triggerDate={true}
                includeCurrentDate={true}
                fromDate={moment(startDate, "YYYY-MM-DD").format("DD/MM/YYYY")}
                toDate={moment(endDate, "YYYY-MM-DD").format("DD/MM/YYYY")}
              />
            </Box>
          </Stack>

          <Paper
            sx={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CustomFilter
              title="OT Report"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={reportRows}
              filteredData={otReportData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              showtitle_count={true}
              downloadFormats={["excel", "csv", "pdf"]}
              includeMetadata={true}
              metadata={reportMetadata}
              newButtonComponent={newButtonComponent}
              toolbarActions={expandAllAction}
              customDownloadHandler={handleOTReportDownload}
            />

            {loader ? (
              <Loader />
            ) : (
              <Mui.Grid
                sx={{
                  ml: 2,
                  mr: 2,
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <OTReportV2Table
                  reportRows={otReportData}
                  employeeIds={visibleEmployeeIds}
                  headers={visibleHeaders}
                  dateColumns={dateColumns}
                  expanded={expandedEmps}
                  onToggle={toggleEmp}
                />
              </Mui.Grid>
            )}
          </Paper>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
